import { useMemo } from 'react';
import { useHorariosPorDia } from './useHorarios';


interface TurmaProfessor {
  id: string;
  nome: string;
  serie: string;
  turno: string;
  aulasSemana: number;
  diasLecionados: Set<string>;
  materias: Set<string>;
}

/**
 * Hook centralizado para extrair turmas do professor a partir dos horarios.
 * Elimina a duplicacao de 6x useHorariosPorDia espalhadas por 4+ arquivos.
 *
 * @param professorId - ID do professor
 * @returns turmas, turmaIds, todosHorarios, isLoading
 */
export function useProfessorTurmas(professorId?: string) {
  const seg = useHorariosPorDia(professorId, 'SEGUNDA');
  const ter = useHorariosPorDia(professorId, 'TERCA');
  const qua = useHorariosPorDia(professorId, 'QUARTA');
  const qui = useHorariosPorDia(professorId, 'QUINTA');
  const sex = useHorariosPorDia(professorId, 'SEXTA');
  const sab = useHorariosPorDia(professorId, 'SABADO');

  const todosHorarios = useMemo(
    () => [...seg.aulas, ...ter.aulas, ...qua.aulas, ...qui.aulas, ...sex.aulas, ...sab.aulas],
    [seg.aulas, ter.aulas, qua.aulas, qui.aulas, sex.aulas, sab.aulas],
  );

  const horariosPorDia = useMemo(() => ({
    SEGUNDA: seg,
    TERCA: ter,
    QUARTA: qua,
    QUINTA: qui,
    SEXTA: sex,
    SABADO: sab,
  }), [seg, ter, qua, qui, sex, sab]);

  const turmas = useMemo(() => {
    const map = new Map<string, TurmaProfessor>();

    todosHorarios.forEach((h: any) => {
      if (!h.turmaId || !h.turma || h.tipo !== 'AULA') return;
      const existing = map.get(h.turmaId);
      if (existing) {
        existing.aulasSemana++;
        if (h.diaSemana) existing.diasLecionados.add(h.diaSemana);
        if (h.materia?.nome) existing.materias.add(h.materia.nome);
      } else {
        const s = new Set<string>();
        const m = new Set<string>();
        if (h.diaSemana) s.add(h.diaSemana);
        if (h.materia?.nome) m.add(h.materia.nome);
        map.set(h.turmaId, {
          id: h.turmaId,
          nome: h.turma.nome || h.turmaId,
          serie: h.turma.serie || '',
          turno: h.turma.turnoOperacional?.nome || '',
          aulasSemana: 1,
          diasLecionados: s,
          materias: m,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [todosHorarios]);

  const turmaIds = useMemo(() => turmas.map(t => t.id), [turmas]);

  const isLoading = seg.isLoading || ter.isLoading || qua.isLoading || qui.isLoading || sex.isLoading || sab.isLoading;

  return {
    turmas,
    turmaIds,
    todosHorarios,
    horariosPorDia,
    isLoading,
  };
}

export type { TurmaProfessor };
